<script setup lang="ts">
const props = defineProps<{
  checkboxId?: string
  checkboxLabel?: string
  checkboxLabelDistance?: 'middle-distance' | 'big-distance' | 'small-distance'
  priorityCheckboxValue?: boolean
  checkboxDisabled?: boolean
  isBoldLabel?: boolean
  isLink?: boolean
  checkboxSize?: 'l'
}>()
const emits = defineEmits(['update:modelValue'])

// TODO: animation with delay
let checkboxIsChecked = ref(false)
let delayOnCheckbox = ref(false)
// delay was true
watch(
  () => props.priorityCheckboxValue,
  (newCheckboxValue) => {
    setCheckboxValue(newCheckboxValue, true)
  }
)

onMounted(() => {
  if (props.priorityCheckboxValue) {
    checkboxIsChecked.value = true
    // delayOnCheckbox.value = false;
  }
})

function setCheckboxValue(value: boolean, handleOutside?: boolean) {
  if (!props.checkboxDisabled || handleOutside) {
    checkboxIsChecked.value = value

    if (!handleOutside) {
      // delayOnCheckbox.value = !delayOnCheckbox.value;
      emits('update:modelValue', checkboxIsChecked.value)
    }
    // else {
    //   delayOnCheckbox.value = !checkboxIsChecked.value;
    // }
  }
}
</script>

<template>
  <div
    class="checkbox"
    :class="[
      checkboxIsChecked && 'active',
      checkboxDisabled && 'disabled',
      checkboxLabelDistance,
      checkboxSize && `checkbox_${checkboxSize}`,
    ]"
  >
    <span
      v-if="checkboxLabel"
      v-html="checkboxLabel"
      @click.stop="setCheckboxValue(!checkboxIsChecked)"
      class="checkbox-text"
      :class="isBoldLabel && 'bold'"
    >
    </span>
    <label class="checkbox-label" :for="checkboxId">
      <svg
        v-if="!checkboxDisabled"
        class="checkbox-mark"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          class="checkbox-mark__line"
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.5123 4.38466C12.8518 4.66751 12.8976 5.17196 12.6148 5.51139L7.61477 11.5114C7.47088 11.6841 7.26098 11.7882 7.03645 11.7984C6.81191 11.8086 6.59345 11.7239 6.43451 11.5649L3.43451 8.56492C3.12209 8.2525 3.12209 7.74597 3.43451 7.43355C3.74693 7.12113 4.25346 7.12113 4.56588 7.43355L6.94644 9.81411L11.3856 4.48709C11.6685 4.14767 12.1729 4.10181 12.5123 4.38466Z"
          fill="white"
        />
      </svg>

      <input
        @change="setCheckboxValue(!checkboxIsChecked)"
        :id="checkboxId"
        :disabled="checkboxDisabled"
        class="checkbox-input"
        type="checkbox"
      />
      <div
        class="checkbox-square"
        :class="delayOnCheckbox && 'with-delay'"
      ></div>
    </label>
  </div>
</template>

<style lang="scss" scoped>
.checkbox {
  display: flex;
  align-items: flex-start;
  flex-direction: row-reverse;
  gap: 12px;

  &_l {
    .checkbox {
      &-text {
        @include font(18px, 24px);
      }

      &-label {
        width: 20px;
        height: 20px;
        margin-top: -1px;
      }
    }
  }

  &.small-distance {
    gap: 8px;
  }

  &.middle-distance {
    gap: 10px;
  }

  &.big-distance {
    gap: 8px;
  }

  &.disabled {
    .checkbox-label {
      background: $control-bg_disabled;
      border-color: $control-border_grey;

      @include mf-breakpoint {
        &:hover {
          cursor: default;
        }
      }
    }

    .checkbox-text {
      color: $content_secondary;
      cursor: default;
    }
  }

  &.active {
    .checkbox-mark__line {
      stroke-dashoffset: 0;
    }

    .checkbox-label {
      border-color: $control-border_orange;
    }

    .checkbox-square {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }

  &-text {
    cursor: pointer;
    @include font(14px, 20px);

    :deep(a) {
      display: inline;
      color: $control-content_orange;

      &:hover:not(:active) {
        @include mf-breakpoint {
          color: $control-content_orange-hover;
        }
      }
    }

    &.bold {
      @include font(18px, 20px, 600);
    }
  }

  @include mf-breakpoint {
    &:not(.disabled) {
      .checkbox-text:hover + .checkbox-label {
        border-color: $control-border_orange;
      }
    }
  }

  &-mark {
    position: absolute;
    z-index: 1;
  }

  &-square {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 100%;
    height: 100%;
    background: $control-bg_primary;
    border-radius: 2px;
    opacity: 0;
    transform: translate(-50%, -50%);

    transition: {
      property: opacity, transform;
      duration: 0.2s;
    }

    &.with-delay {
      transition-delay: 0.2s;
    }
  }

  &-label {
    margin-top: 2px;
    flex-shrink: 0;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    padding: 4px;
    border-radius: 4px;
    border: 1px solid $control-border_grey;
    background: $bg_page;
    cursor: pointer;
    transition: 0.3s border-color, 0.3s background-color;

    @include mf-breakpoint {
      &:hover {
        border-color: $control-border_orange;
      }
    }
  }

  &-mark {
    &__line {
      stroke-dasharray: 20px;
      stroke-dashoffset: 20px;
      fill: $content_white !important;
      transition: stroke-dashoffset 0.1s ease-out;

      &.with-delay {
        transition-delay: 0.2s;
      }
    }
  }

  &-input {
    display: none;
  }
}
</style>
